import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Container, Logo, PageMeta, Notice, Loading } from "@/components/site/Bits";
import { Field, TextInput } from "@/components/site/Form";

const Shell = ({ title, intro, children, testId }) => (
  <div className="bg-pg-pale py-14 md:py-20" data-testid={testId}>
    <Container>
      <div className="mx-auto max-w-md">
        <div className="mb-8 flex justify-center">
          <Logo showName={false} testId="auth-logo" />
        </div>
        <div className="rounded-lg border border-pg-border bg-white p-7 md:p-8">
          <h1 className="text-2xl font-bold text-pg-dark">{title}</h1>
          <p className="mt-2 text-sm leading-relaxed text-pg-muted">{intro}</p>
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </Container>
  </div>
);



export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ loading: false, sent: false, error: "" });

  const submit = async (event) => {
    event.preventDefault();
    setState({ loading: true, sent: false, error: "" });
    try {
      await api.post("/auth/forgot-password", { email });
      setState({ loading: false, sent: true, error: "" });
    } catch (error) {
      setState({ loading: false, sent: false, error: apiError(error) });
    }
  };

  return (
    <Shell
      title="Forgotten your password?"
      intro="Enter the email address you use for your Procure Group account and we will send you a link to choose a new password."
      testId="forgot-password-page"
    >
      <PageMeta title="Reset your password | Procure Group" description="Request a link to reset your Procure Group account password." robots="noindex, nofollow" />
      {state.sent ? (
        <div className="space-y-5" data-testid="forgot-password-sent">
          <Notice>
            If an account exists for <strong>{email}</strong>, a link to choose a new password is on
            its way. It is valid for one hour. Please check your junk folder if it does not arrive
            within a few minutes.
          </Notice>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
          <Field label="Email address" required>
            <TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                       data-testid="forgot-password-email" autoComplete="email" />
          </Field>
          {state.error && <Notice tone="error" testId="forgot-password-error">{state.error}</Notice>}
          <button
            type="submit"
            disabled={state.loading || !email}
            data-testid="forgot-password-submit"
            className="w-full rounded-md bg-pg-primary px-4 py-3 text-sm font-semibold text-white hover:bg-pg-dark disabled:opacity-50 transition-colors"
          >
            {state.loading ? "Sending…" : "Send me a reset link"}
          </button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token") || "";
  const [values, setValues] = useState({ password: "", confirm: "" });
  const [state, setState] = useState({ loading: false, done: false, error: "" });

  const submit = async (event) => {
    event.preventDefault();
    if (values.password.length < 8)
      return setState({ loading: false, done: false, error: "Please choose a password of at least 8 characters." });
    if (values.password !== values.confirm)
      return setState({ loading: false, done: false, error: "The two passwords do not match." });
    setState({ loading: true, done: false, error: "" });
    try {
      await api.post("/auth/reset-password", { token, password: values.password });
      setState({ loading: false, done: true, error: "" });
    } catch (error) {
      setState({ loading: false, done: false, error: apiError(error) });
    }
  };

  return (
    <Shell
      title="Choose a new password"
      intro="Set a new password for your Procure Group account, then sign in with it."
      testId="reset-password-page"
    >
      <PageMeta title="Choose a new password | Procure Group" description="Set a new password for your Procure Group account." robots="noindex, nofollow" />
      {!token ? (
        <div className="space-y-5">
          <Notice tone="error" testId="reset-password-no-token">
            This link is incomplete. Please request a new password reset link.
          </Notice>
          <Link to="/forgot-password" className="pg-link font-semibold" data-testid="reset-password-request-again">
            Request a new link
          </Link>
        </div>
      ) : state.done ? (
        <div className="space-y-5" data-testid="reset-password-done">
          <Notice>
            Your password has been changed. You can now sign in with it from the sign-in page you
            normally use.
          </Notice>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
          <Field label="New password" hint="At least 8 characters." required>
            <TextInput type="password" value={values.password} autoComplete="new-password"
                       onChange={(e) => setValues({ ...values, password: e.target.value })}
                       data-testid="reset-password-new" />
          </Field>
          <Field label="Confirm new password" required>
            <TextInput type="password" value={values.confirm} autoComplete="new-password"
                       onChange={(e) => setValues({ ...values, confirm: e.target.value })}
                       data-testid="reset-password-confirm" />
          </Field>
          {state.error && <Notice tone="error" testId="reset-password-error">{state.error}</Notice>}
          <button
            type="submit"
            disabled={state.loading}
            data-testid="reset-password-submit"
            className="w-full rounded-md bg-pg-primary px-4 py-3 text-sm font-semibold text-white hover:bg-pg-dark disabled:opacity-50 transition-colors"
          >
            {state.loading ? "Saving…" : "Save my new password"}
          </button>
          <p className="text-center text-sm text-pg-muted">
            Link expired?{" "}
            <Link to="/forgot-password" className="pg-link font-semibold">Request a new one</Link>
          </p>
        </form>
      )}
    </Shell>
  );
}

export function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(location.hash.replace("#", "")).get("session_id");
    if (!sessionId) {
      navigate("/staff", { replace: true });
      return;
    }
    api
      .post("/auth/session", { session_id: sessionId }, { headers: { "X-Session-ID": sessionId } })
      .then(({ data }) => {
        setUser(data);
        const destination = ["admin", "office", "surveyor", "technician", "subcontractor"]
          .includes(data.role) ? "/admin" : "/";
        window.history.replaceState({}, "", destination);
        navigate(destination, { replace: true, state: { user: data } });
      })
      .catch((err) => setError(apiError(err)));
  }, [location.hash, navigate, setUser]);

  if (error)
    return (
      <Shell title="Sign-in could not be completed" intro="Please try signing in again." testId="auth-callback-error">
        <Notice tone="error">{error}</Notice>
        <Link to="/staff" className="mt-5 block rounded-md bg-pg-primary px-4 py-3 text-center text-sm font-semibold text-white">
          Back to sign in
        </Link>
      </Shell>
    );

  return <Loading label="Completing sign-in" testId="auth-callback-loading" />;
}
